import globals from "/utils/globals";
import { Notifier } from "@airbrake/browser";

const {
  id, key, fingerprint
} = globals.airbrake || {};
let airbrake;

const init = () => {

  airbrake = new Notifier({
    projectId: globals.airbrake.id,
    projectKey: globals.airbrake.key,
    // disable global error catching because it produces too much spam
    instrumentation: { onerror: false }
  });

  airbrake.addFilter(notice => {
    // these errors most probably come from autofill plugin
    // see here: https://stackoverflow.com/questions/43399818/securityerror-from-facebook-and-cross-domain-messaging/43469069#43469069
    // and here: https://bugs.chromium.org/p/chromium/issues/detail?id=590375
    let error;
    try {
      for (let i = 0; i < notice.errors.length; ++i) {
        error = notice.errors[i];
        if (error.message.match(/Blocked a frame with origin .* from accessing a frame with origin/)) {
          return null;
        }
      }
    } catch (_e) {
      // NOTE: it would be silly to have an error in error reporting ;-)
    }

    return notice;
  });

  // Silence Facebook & extension errors. Derived from:
  // https://github.com/getsentry/sentry/blob/c9c352b377bb8225c71c63a2adab2d4255dc7114/src/sentry/filters/browser_extensions.py#L50
  // HTMLDocument.<anonymous> is from: https://blinkist.airbrake.io/projects/120535/groups/2193180230694040756?tab=notice-detail
  airbrake.addFilter(notice => {
    const err = notice.errors[0];
    const sourcesToIgnore = /graph\.facebook\.com|connect\.facebook\.net|^chrome(?:-extension)?:\/\/|HTMLDocument\.<anonymous>/;

    if (err.backtrace && err.backtrace.length > 0) {
      const frame = err.backtrace[0];
      if (sourcesToIgnore.test(frame.file)) {
        return null;
      }
    }

    return notice;
  });

  // Silence errors caused by Facebook crawlers
  // See this issue: https://github.com/aFarkas/lazysizes/issues/520
  // And this airbrake: https://blinkist.airbrake.io/projects/190497/groups/2265315420464617760?tab=overview
  airbrake.addFilter(notice => {
    const err = notice.errors[0];
    if (err.message === "TypeError: Illegal invocation" && location.search.indexOf("fbclid") > -1) {
      return null;
    } else {
      return notice;
    }
  });

  // Filter out standard network errors that are no programming errors but client connection issues
  airbrake.addFilter(notice => {
    const ignoredNetworkErrors = [
      "Failed to fetch",
      "Aborted",
      "The operation was aborted",
      "cancelled",
      "Abgebrochen",
      "avbruten",
      "Timeout",
      "cancelado",
      "Zeitüberschreitung bei der Anforderung.",
      "NetworkError when attempting to fetch resource",
      "Network request failed",
      "An SSL error has occurred and a secure connection to the server cannot be made",
      "A server with the specified hostname could not be found",
      "Es wurde kein Server mit dem angegebenen Hostnamen gefunden",
      "The internet connection appears to be offline",
      "Es besteht anscheinend keine Verbindung zum Internet"
    ];

    const err = notice.errors[0];
    for (const networkError of ignoredNetworkErrors) {
      if (err.message.indexOf(networkError) > -1) {
        return null;
      }
    }

    return notice;
  });

  // this filter ensures we report errors in our JS assets or packs
  airbrake.addFilter(notice => {
    notice.context.severity = "warning";

    const err = notice.errors[0];
    const validAssetFileNames = /(\/assets\/|\/packs\/)/;

    if (err.backtrace && err.backtrace.length > 0) {
      const frame = err.backtrace[0];

      if (validAssetFileNames.test(frame.file)) {
        notice.context.severity = "error";
      }
    }

    // This will filter out [object Event] errors. They should already be
    // transformed and reported via the onerror handler at the top.
    airbrake.addFilter(function(notice) {
      var err = notice.errors[0];
      if (err.message === "[object Event]") {
        return null;
      } else {
        return notice;
      }
    });

    return notice;
  });

  airbrake.addFilter(notice => {
    notice.context.environment = globals.env;
    // eslint-disable-next-line camelcase
    notice.session.fingerprinted_user_id = fingerprint;

    return notice;
  });
};

if (id && key) {
  init();
}

export default airbrake;
